import dayjs from 'dayjs';

import colors from '@totem/styles/colors';

// Event severities as stored and shown elsewhere in the portal: 5 Critical, 4 Major, 3 Minor, 2 Warning,
// 1 Information, 0 OK. Findings are Minor and above.
export const SEVERITIES = [
  {
    value: 5,
    key: 'critical',
    label: 'Critical',
    color: colors.event.criticality.background.critical,
  },
  {
    value: 4,
    key: 'major',
    label: 'Major',
    color: colors.event.criticality.background.major,
  },
  {
    value: 3,
    key: 'minor',
    label: 'Minor',
    color: colors.event.criticality.background.minor,
  },
  {
    value: 2,
    key: 'warning',
    label: 'Warning',
    color: colors.event.criticality.background.warning,
  },
  {
    value: 1,
    key: 'information',
    label: 'Information',
    color: colors.event.criticality.background.info,
  },
  {
    value: 0,
    key: 'ok',
    label: 'OK',
    color: colors.event.criticality.background.ok,
  },
] as const;

export type SeverityKey = (typeof SEVERITIES)[number]['key'];

export const FINDING_SEVERITIES = SEVERITIES.filter(
  (severity) => severity.value >= 3,
);

export const getSeverity = (value: number) =>
  SEVERITIES.find((severity) => severity.value === Math.min(5, value)) ??
  SEVERITIES[SEVERITIES.length - 1];

// Age of an open (or time to resolve of a resolved) finding, styled from fresh to long-running.
export const getAgeClass = (days: number) => {
  if (days < 1) {
    return 'age-fresh';
  }
  if (days < 3) {
    return 'age-recent';
  }
  if (days < 7) {
    return 'age-aging';
  }
  if (days < 30) {
    return 'age-stale';
  }
  return 'age-ancient';
};

export const formatAge = (days: number) => {
  if (days < 1) {
    return days * 24 < 1 ? 'under 1h' : `${Math.round(days * 24)}h`;
  }
  return `${Math.round(days)}d`;
};

export const formatDuration = (hours: number) => {
  if (hours < 1) {
    return `${Math.max(1, Math.round(hours * 60))} min`;
  }
  if (hours < 48) {
    return `${Math.round(hours)} hours`;
  }
  return `${Math.round(hours / 24)} days`;
};

export const formatDateTime = (date: string | null) =>
  date ? dayjs(date).format('MMM D, YYYY h:mm A') : '—';

export const formatDate = (date: string | null) =>
  date ? dayjs(date).format('MMM D, YYYY') : '—';

export const EMPTY_ID = '000000000000000000000000';

export const ticketTypeFromNumber = (ticketNumber: string) => {
  const prefixes: Record<string, string> = {
    INC: 'Incident',
    SVC: 'Service Request',
    CHG: 'Change Request',
    PRB: 'Problem',
    REQ: 'Request',
    TASK: 'Task',
  };
  const prefix = Object.keys(prefixes).find((key) =>
    ticketNumber.toUpperCase().startsWith(key),
  );
  return prefix ? prefixes[prefix] : 'Ticket';
};
